import { Dictionary, groupBy, merge } from 'lodash';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Role, Team, User } from 'api/models';
import { ResourceManagementConfig } from 'api/resource-management/types';
import { DemandRoleSummary } from 'components/organisms/resource-management/common-types/demand';
import { DemandEvents } from 'components/organisms/resource-management/event-types';
import { useDemandViewFilter } from 'components/organisms/resource-management/filters';
import {
  useDemandLoader,
  useResourcesLoader,
  useViewPresetManager,
} from 'components/organisms/resource-management/managers';
import { getCalendars } from 'components/organisms/resource-management/scheduler-config/calendars';
import { useColumnsConfigForDemand } from 'components/organisms/resource-management/scheduler-config/columns/columns-for-demand';
import { useEventsTooltip } from 'components/organisms/resource-management/scheduler-config/event-tooltips';
import {
  AllocationEventModel,
  DemandModel,
} from 'components/organisms/resource-management/scheduler-config/models';
import { DemandViewPresetStyles } from 'components/organisms/resource-management/scheduler-config/styles';
import {
  getDemandViewZoomPresets,
  UserSelectablePresetIdForDemand,
} from 'components/organisms/resource-management/scheduler-config/view-presets/demand-view-presets';
import { Scheduler as RMScheduler } from 'components/organisms/resource-management/scheduler/scheduler';
import { useDemandViewPresetState } from 'components/organisms/resource-management/scheduler/use-demand-zoom-state';
import { SecondaryToolbar } from 'components/organisms/resource-management/secondary-toolbar';
import { ProjectsWithMembers } from 'components/organisms/resource-management/types';
import { convertRolesIntoResourceTree } from 'components/organisms/resource-management/utils/demand-resource-tree';
import { asDemandCapacityEvent } from 'components/organisms/resource-management/utils/events-conversion/demand/capacity';
import { asDemandRoleSummaryEvent } from 'components/organisms/resource-management/utils/events-conversion/demand/role-summary';
import {
  asTeamTotalAllocationEvent,
  asTeamTotalHardAllocationEvent,
  asTeamTotalSoftAllocationEvent,
  asTotalAllocationEvent,
  asUserTotalAllocationEvent,
  asUserTotalHardAllocationEvent,
  asUserTotalSoftAllocationEvent,
} from 'components/organisms/resource-management/utils/events-conversion/demand/total-allocation';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { ResourceManagementPage } from '../page';

const Scheduler = styled(RMScheduler)`
  ${DemandViewPresetStyles};
`;

interface Props {
  roles: Role[];
  users: User[];
  teams: Team[];
  projects: ProjectsWithMembers[];
  roleLookup: Dictionary<Role>;
  userLookup: Dictionary<User>;
  teamLookup: Dictionary<Team>;
  projectLookup: Dictionary<ProjectsWithMembers>;
  teamMembership: Record<string, Team[]>;
  workingDaysConfig: WorkingDaysConfiguration;
  resourceManagementConfig?: ResourceManagementConfig;
}

export const ResourceManagementDemandPage: FC<Props> = ({
  users,
  workingDaysConfig,
  roles: allRoles,
  userLookup,
  resourceManagementConfig,
}) => {
  const schedulerRef = useRef<any>();
  const [searchText, setSearchText] = useState('');

  const { viewPreset, isStateRestored, setViewPreset } =
    useDemandViewPresetState();

  const zoomPresets = useMemo(
    () => getDemandViewZoomPresets(workingDaysConfig),
    [workingDaysConfig],
  );

  const roleIdList = useMemo(
    () => allRoles.map(({ roleId }) => roleId),
    [allRoles],
  );

  const usersGroupedByRole = useMemo(
    () =>
      merge(
        groupBy(users, 'role.roleId'),
        allRoles.reduce((r, x) => ({ ...r, [x.roleId]: [] }), {}),
      ),
    [allRoles, users],
  );

  const rolesToUserCount: Dictionary<number> = useMemo(
    () =>
      allRoles.reduce(
        (r, { roleId }) => ({
          ...r,
          [roleId]: usersGroupedByRole[roleId].length,
        }),
        {},
      ),
    [allRoles, usersGroupedByRole],
  );
  const rolesToUserCountByUserType = useMemo(() => {
    return allRoles.reduce((r, { roleId }) => {
      const usersInRole = usersGroupedByRole[roleId];
      const groupByUserType = groupBy(usersInRole, 'userType');

      return {
        ...r,
        [roleId]: Object.entries(groupByUserType).reduce(
          (result, [userType, usersInParticularType]) => ({
            ...result,
            [userType]: usersInParticularType.length,
          }),
          {},
        ),
      };
    }, {});
  }, [allRoles, usersGroupedByRole]);

  const onAllocationLoaded = useCallback(
    (demandSummaries: DemandRoleSummary[]) =>
      new Promise<void>((resolve) => {
        const demandEvents: DemandEvents[] = [];

        demandEvents.push(
          ...demandSummaries.map(asDemandRoleSummaryEvent),
          ...demandSummaries.map(asDemandCapacityEvent),
          ...demandSummaries.map(asTotalAllocationEvent),
          ...(resourceManagementConfig?.softAllocations.enabled
            ? [
                ...demandSummaries.map(asUserTotalSoftAllocationEvent),
                ...demandSummaries.map(asUserTotalHardAllocationEvent),
                ...demandSummaries.map(asTeamTotalSoftAllocationEvent),
                ...demandSummaries.map(asTeamTotalHardAllocationEvent),
              ]
            : [
                ...demandSummaries.map(asUserTotalAllocationEvent),
                ...demandSummaries.map(asTeamTotalAllocationEvent),
              ]),
        );

        const nonDuplicateEvents = demandEvents.filter(
          (event) =>
            !schedulerRef.current.instance.eventStore.isAvailable(event.id),
        );

        if (nonDuplicateEvents.length > 0) {
          schedulerRef.current.instance.suspendRefresh();
          schedulerRef.current.instance.eventStore.add(nonDuplicateEvents);
          schedulerRef.current.instance.resumeRefresh(true);
        }

        resolve();
      }),
    [resourceManagementConfig?.softAllocations.enabled],
  );

  const getSchedulerProjectInfo = useCallback(() => {
    const resources = convertRolesIntoResourceTree(
      allRoles,
      rolesToUserCount,
      rolesToUserCountByUserType,
      resourceManagementConfig?.softAllocations.enabled,
    );

    return {
      resourceStore: {
        modelClass: DemandModel,
        data: resources,
        useRawData: {
          disableDuplicateIdCheck: true,
          disableDefaultValue: true,
          disableTypeConversion: true,
        },
        verifyNoGeneratedIds: false,
        reapplySortersOnAdd: true,
      },
      eventStore: {
        modelClass: AllocationEventModel,
        useRawData: {
          disableDuplicateIdCheck: true,
          disableDefaultValue: true,
          disableTypeConversion: true,
        },
        verifyNoGeneratedIds: false,
      },
      calendarManagerStore: {
        useRawData: true,
        data: getCalendars(workingDaysConfig),
      },
      delayCalculation: false,
      calendar: 'nonWorkingDays',
    };
  }, [
    allRoles,
    resourceManagementConfig?.softAllocations.enabled,
    rolesToUserCount,
    rolesToUserCountByUserType,
    workingDaysConfig,
  ]);

  const columnConfig = useColumnsConfigForDemand({ schedulerRef });

  const { isResourceStoreLoaded } = useResourcesLoader({
    schedulerRef,
    workingDaysConfig,
    projectInfoProvider: getSchedulerProjectInfo,
  });

  useDemandLoader({
    schedulerRef,
    roleIdList,
    currentViewPreset: viewPreset,
    workingDaysConfig,
    onAllocationLoadHandler: onAllocationLoaded,
  });

  useEventsTooltip(schedulerRef, userLookup);

  useViewPresetManager({
    loading: !isStateRestored,
    schedulerRef,
    currentPresetId: viewPreset,
    onPresetChange: useCallback(
      (viewPresetId) => {
        schedulerRef.current.instance.suspendRefresh();
        schedulerRef.current.instance.eventStore.removeAll();
        schedulerRef.current.instance.resumeRefresh(true);
        schedulerRef.current.instance.trigger('onViewPresetChange');
        setViewPreset(viewPresetId);
      },
      [setViewPreset],
    ),
  });

  useDemandViewFilter({
    roles: allRoles,
    schedulerRef,
    onReset: () => setSearchText(''),
    canApplyFilter: isResourceStoreLoaded,
    searchText,
  });

  return (
    <ResourceManagementPage
      actions={{
        secondary: (
          <SecondaryToolbar
            zoomPresets={zoomPresets}
            viewPresetIds={UserSelectablePresetIdForDemand}
            currentPreset={viewPreset}
            searchText={searchText}
            onViewPresetChange={(presetId) => {
              schedulerRef.current.instance.viewPreset = presetId;
            }}
            shiftNext={() => {
              schedulerRef.current.instance.shiftNext();
            }}
            shiftPrevious={() => {
              schedulerRef.current.instance.shiftPrevious();
            }}
            goToToday={() => {
              schedulerRef.current.instance.scrollToNow({
                block: 'center',
              });
            }}
            onSearchChange={(text) => setSearchText(text)}
          />
        ),
      }}
      content={
        <Scheduler
          ref={schedulerRef}
          columns={columnConfig}
          newEventAllowedOn={useMemo(() => [], [])}
          zoomPresets={zoomPresets}
          workingDaysConfig={workingDaysConfig}
          resourceManagementConfig={resourceManagementConfig}
        />
      }
    />
  );
};
